import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { GenericField, OId } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { useAnimalProfileStore, useSessionStore } from '@Stores';
import { useForm } from '@FormModule';

interface MedicalStaff {
  [key: string]: any;
  first_name: string;
  last_name: string;
  _id: OId;
}

const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formError = ref();

export default function () {
  const { t } = useI18n();
  const { getDetailsSchema, wasFormSubmitted } = useForm();
  const { medicalStaff, diagnosticTemplates } = storeToRefs(useAnimalProfileStore());
  const { shelterProfile } = storeToRefs(useSessionStore());

  const rawFields = ref<RawField[]>([
    {
      label: 'Diagnostic',
      name: 'template_name',
      required: true,
      type: 'select',
      value: '',
    },
    {
      label: 'Status',
      name: 'action',
      required: true,
      type: 'radio',
      value: '',
      values: [t('common.schedule'), t('common.complete')],
    },
    {
      label: 'Diagnostic Date',
      name: 'date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'Product',
      name: 'product',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Manufacturer',
      name: 'manufacturer',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Notes',
      name: 'notes',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: '',
      name: 'separator',
      required: false,
      type: 'seperator',
      value: '',
    },
    {
      label: 'Results',
      name: 'results',
      required: true,
      type: 'result',
      value: '',
    },
    {
      label: 'Administered By',
      name: 'by',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Other Veterinarian',
      name: 'other_by',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Schedule a Follow-Up Check',
      name: 'followup_test',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: 'Time Until Follow-Up',
      name: 'next_test',
      required: true,
      type: 'timeframe',
      value: '',
    },
    {
      label: 'Follow-Up Results',
      name: 'followup_results',
      required: false,
      type: 'result',
      value: '',
    },
    {
      label: 'Set Default Costs',
      name: 'track_medical_costs',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: 'Clinic Cost',
      name: 'clinic_cost',
      required: false,
      type: 'money',
      value: '',
    },
    {
      label: 'Retail Cost',
      name: 'retail_cost',
      required: false,
      type: 'money',
      value: '',
    },
  ]);

  const attendingVetOptions = computed(() =>
    medicalStaff.value.map((staff: MedicalStaff) => {
      const { first_name, last_name } = staff;
      return {
        ...staff,
        by: `${first_name} ${last_name}`,
      };
    })
  );

  const fieldsSettings = computed(
    (): FieldInitialSettings => ({
      action: {
        settings: {
          hint: 'animals.individualPage.medical.medications.completeMedicationHint',
        },
      },
      template_name: {
        edit: {
          label: 'template_name',
          options: diagnosticTemplates.value.sort((a, b) =>
            a.template_name.localeCompare(b.template_name, undefined, { numeric: true })
          ),
        },
      },
      by: {
        edit: {
          label: 'by',
          options: [...attendingVetOptions.value, { by: t('common.other') }],
        },
        settings: {
          required: false,
          show: false,
        },
      },
      other_by: {
        settings: {
          show: false,
        },
      },
      track_medical_costs: {
        settings: {
          show: !!shelterProfile.value?.track_medical_costs,
        },
      },
      clinic_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
        },
      },
      retail_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
        },
      },
    })
  );

  const surgeryTemplateField = computed(() => fieldsSchema.value.find(({ name }) => name === 'template_name'));
  const followUpFields = computed(() => fieldsSchema.value.find(({ name }) => name === 'followup_test'));
  const byField = computed(() => fieldsSchema.value.find(({ name }) => name === 'by'));
  const byModel = computed(() => byField.value?.model);
  const actionField = computed(() => fieldsSchema.value.find(({ name }) => name === 'action'));
  const trackMedicalCostsField = computed(() => fieldsSchema.value.find(({ name }) => name === 'track_medical_costs'));
  const trackMedicalCostsModel = computed(() => trackMedicalCostsField.value?.model);
  const isAction = computed(() => actionField.value?.model);
  const isFollowUp = computed(() => followUpFields.value?.model);
  const surgeryTemplateModel = computed(() => surgeryTemplateField.value?.model);

  watch(isAction, () => showActionFields() && showFollowUpFields());
  watch(isFollowUp, () => showFollowUpFields());
  watch(surgeryTemplateModel, (newValue, oldValue) => fillFormWithTemplate(newValue, oldValue));
  watch(byModel, () => showOtherVetField());
  watch(trackMedicalCostsModel, () => showMedicalCostsFields());

  function showMedicalCostsFields() {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.settings = {
        ...field.settings,
        show: trackMedicalCostsModel.value,
        required: trackMedicalCostsModel.value,
      };
      field.state?.errors?.clear();
    });
  }

  function showOtherVetField() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'other_by') {
        const showField = byModel?.value?.by === t('common.other');

        field.settings = {
          ...field.settings,
          show: showField,
          required: showField,
        };

        if (byModel?.value?.by !== t('common.other')) {
          field.model = '';
          field.value = '';
          field.state.errors.clear();
        }
      }
    });

    return true;
  }

  function fillFormWithTemplate(newValue: any, oldValue: any) {
    if (!newValue || oldValue === newValue) return;

    updateSchemaModels(surgeryTemplateModel.value);
  }

  function showActionFields() {
    fieldsSchema.value.forEach((field) => {
      const showField = isAction.value === 'Complete';

      if (field.name === 'by') {
        field.settings = {
          ...field.settings,
          required: showField,
          show: showField,
        };
        field.model = '';
        field.value = '';
        field.state.errors.clear();
      }

      if (field.name === 'results') {
        field.settings = {
          ...field.settings,
          required: showField,
          scheduling: !showField,
        };
        field.state.errors.clear();
      }
    });

    return true;
  }

  function showFollowUpFields() {
    fieldsSchema.value.forEach((field) => {
      const showField = isFollowUp.value;
      const requireField = isAction.value === 'Complete';

      if (field.name === 'next_test') {
        field.settings = {
          ...field.settings,
          required: showField,
          show: showField,
        };
        field.state.errors.clear();
      }

      if (field.name === 'followup_results') {
        field.settings = {
          ...field.settings,
          required: showField,
          show: showField,
          scheduling: !requireField,
        };
        field.state.errors.clear();
      }
    });

    return true;
  }

  function setForm() {
    fieldsSchema.value = getDetailsSchema(rawFields.value, fieldsSettings.value);

    if (!surgeryTemplateModel.value) {
      fieldsSchema.value.forEach((field: any) => {
        if (field.name === 'template_name') return;

        field.state.disabled = true;
        field.settings.disabled = true;
      });
    }
  }

  function updateSchemaModels(sourceData?: any) {
    const toggleableFields = ['next_test', 'template_name', 'clinic_cost', 'retail_cost'];

    formError.value = null;
    wasFormSubmitted.value = false;

    fieldsSchema.value.forEach((field) => {
      field.state.disabled = false;
      field.settings.disabled = false;

      const match = sourceData[field.name];
      if (toggleableFields.includes(field.name)) return;

      switch (field.name) {
        case 'action': {
          setMatchStatusField(field, sourceData);
          break;
        }
        case 'by': {
          setByField(field, sourceData);
          break;
        }
        case 'other_by': {
          setOtherByField(field, sourceData);
          break;
        }
        case 'track_medical_costs': {
          setMatchField(field, match || false);
          showMedicalCostsFields();
          nextTick(() => {
            fillMedicalCostsFields(sourceData);
          });
          break;
        }
        case 'followup_test': {
          setMatchField(field, match);
          showFollowUpFields();
          nextTick(() => {
            setMatchFollowUpField(sourceData);
          });
          break;
        }
        case 'date': {
          field.model = new Date();
          field.value = new Date();
          break;
        }
        default: {
          setMatchField(field, match);
        }
      }
    });
  }

  function setMatchField(field: any, match: any) {
    field.model = match;
    field.value = match;
  }

  function setMatchStatusField(field: any, sourceData: any) {
    field.model = sourceData.status === 'active' ? 'Schedule' : 'Complete';
    field.value = sourceData.status === 'active' ? 'Schedule' : 'Complete';
    field.state.errors.clear();
  }

  function setByField(field: any, sourceData: any) {
    if (!sourceData.by) return;

    if (sourceData.by_id === 'other') {
      field.model = { by: 'Other' };
      field.value = { by: 'Other' };

      return;
    }

    const fullStaffData = medicalStaff.value.find((staff: MedicalStaff) => staff._id?.$oid === sourceData.by_id?.$oid);
    if (!fullStaffData) return;

    const { first_name, last_name, _id } = fullStaffData;
    const by = { _id, first_name, last_name, by: sourceData.by };

    field.model = by;
    field.value = by;
  }

  function fillMedicalCostsFields(sourceData: any) {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.model = Number(sourceData.medical_costs_settings?.[field.name]);
      field.value = Number(sourceData.medical_costs_settings?.[field.name]);
      field.state.errors.clear();
    });
  }

  function setOtherByField(field: any, sourceData: any) {
    if (!sourceData.by) return;

    field.model = sourceData.by;
    field.value = sourceData.by;
  }

  function setMatchFollowUpField(sourceData: any) {
    const followUpFields = ['next_test'];

    fieldsSchema.value.forEach((field) => {
      if (!followUpFields.includes(field.name)) return;

      field.state.errors.clear();

      const timeFrame = {
        duration: sourceData.next_test,
        duration_unit: sourceData.next_test_unit,
      };

      switch (field.name) {
        case 'next_test': {
          field.model = timeFrame;
          field.value = timeFrame;
          break;
        }
      }
    });
  }

  function resetForm() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    formError,
    rawFields,

    resetForm,
    setForm,
  };
}
